import { type LayerKind, LayerKindName } from "@/layers/utility/createLayerObject";
import {
  type CoordinateSystem,
  type GenericGeoJsonRequest,
  type GeoJSONFeatureCollection,
  WORLD_BBOX,
  type WfsRequest,
} from "@mm/api.meteomatics.com";

export type BBox = { west: number; south: number; east: number; north: number };
export const WFS_TILE_ZOOM = 1;

// Fixed tile zoom for GeoJSON endpoint tiling (viewport-independent caching).
const GEOJSON_TILE_ZOOM_JETSTREAM = 0;
const GEOJSON_TILE_ZOOM_THUNDERSTORM = 3;
const GEOJSON_TILE_ZOOM_LIGHTNING = 2;
// Viewport buffer so tile set is stable across minor pans/zooms.
const GEOJSON_VIEWPORT_BUFFER_RATIO_JETSTREAM = 0.15;
const GEOJSON_VIEWPORT_BUFFER_RATIO_THUNDERSTORM = 0.05;
const GEOJSON_VIEWPORT_BUFFER_RATIO_LIGHTNING = 0.05;
// Add one tile ring around viewport tiles to reduce churn while panning.
const GEOJSON_TILE_PADDING_JETSTREAM = 1;
const GEOJSON_TILE_PADDING_THUNDERSTORM = 0;
const GEOJSON_TILE_PADDING_LIGHTNING = 0;

const MAX_MERCATOR_LAT = 85.05112878;

/**
 * Return per-layer tile configuration (zoom, viewport buffer, padding).
 */
export function getGeoJsonTileConfig(kind?: LayerKind) {
  if (kind === LayerKindName.LightningLayerDescription) {
    return {
      zoom: GEOJSON_TILE_ZOOM_LIGHTNING,
      viewportBufferRatio: GEOJSON_VIEWPORT_BUFFER_RATIO_LIGHTNING,
      padding: GEOJSON_TILE_PADDING_LIGHTNING,
    };
  }
  if (kind === LayerKindName.ThunderstormTrackingLayerDescription) {
    return {
      zoom: GEOJSON_TILE_ZOOM_THUNDERSTORM,
      viewportBufferRatio: GEOJSON_VIEWPORT_BUFFER_RATIO_THUNDERSTORM,
      padding: GEOJSON_TILE_PADDING_THUNDERSTORM,
    };
  }
  // Default to Jetstream settings.
  return {
    zoom: GEOJSON_TILE_ZOOM_JETSTREAM,
    viewportBufferRatio: GEOJSON_VIEWPORT_BUFFER_RATIO_JETSTREAM,
    padding: GEOJSON_TILE_PADDING_JETSTREAM,
  };
}

/**
 * Build a stable cache id for a single WFS tile (independent of viewport bbox).
 */
export function buildWfsTileCacheId(
  req: WfsRequest<CoordinateSystem.WGS84>,
  tileKey: string, // e.g. "x10-y7-z4"
): string {
  const m = req.model;
  const params = Array.isArray(req.parameters) ? req.parameters.join(",") : String(req.parameters);
  const dt =
    typeof req.datetime === "string" ? req.datetime : (req.datetime as any)?.toString?.() ?? String(req.datetime);
  const bbox = req.boundingBoxLimit || WORLD_BBOX;
  const bboxKey = `w${bbox.west},s${bbox.south},e${bbox.east},n${bbox.north}`;
  return `wfs:${m}|p:${params}|t:${dt}|bbox:${bboxKey}|tile:${tileKey}`;
}

/**
 * Build a stable aggregate cache id for a WFS tile window (independent of viewport bbox).
 */
export function buildWfsTilesAggregateCacheId(
  req: WfsRequest<CoordinateSystem.WGS84>,
  z: number,
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number,
): string {
  const m = req.model;
  const params = Array.isArray(req.parameters) ? req.parameters.join(",") : String(req.parameters);
  const dt =
    typeof req.datetime === "string" ? req.datetime : (req.datetime as any)?.toString?.() ?? String(req.datetime);
  const bbox = req.boundingBoxLimit || WORLD_BBOX;
  const bboxKey = `w${bbox.west},s${bbox.south},e${bbox.east},n${bbox.north}`;
  return `wfs:${m}|p:${params}|t:${dt}|bbox:${bboxKey}|tiles:z${z}:x${xMin}-${xMax}:y${yMin}-${yMax}`;
}

export type WfsTileWindow = {
  visibleTiles: Array<{ x: number; y: number; z: number; bbox: BBox }>;
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  aggregateCacheId: string;
};

export type GeoJsonTile = { x: number; y: number; z: number; bbox: BBox };

export function getWfsTileWindow(request: WfsRequest<CoordinateSystem.WGS84>, z: number): WfsTileWindow | null {
  const viewport: BBox = {
    west: request.area.west,
    east: request.area.east,
    south: request.area.south,
    north: request.area.north,
  };
  const visibleTiles = getVisibleTilesWithBBox(viewport, z);
  if (visibleTiles.length === 0) {
    return null;
  }
  const xMin = Math.min(...visibleTiles.map((t) => t.x));
  const xMax = Math.max(...visibleTiles.map((t) => t.x));
  const yMin = Math.min(...visibleTiles.map((t) => t.y));
  const yMax = Math.max(...visibleTiles.map((t) => t.y));
  return {
    visibleTiles,
    xMin,
    xMax,
    yMin,
    yMax,
    aggregateCacheId: buildWfsTilesAggregateCacheId(request, z, xMin, xMax, yMin, yMax),
  };
}

/**
 * Compute visible + padded XYZ tiles for a viewport at fixed per-layer tile config.
 */
export function getGeoJsonTilesForViewport(viewport: BBox, kind?: LayerKind): GeoJsonTile[] {
  const tileConfig = getGeoJsonTileConfig(kind);
  const baseTiles = getVisibleTilesWithBBox(viewport, tileConfig.zoom, tileConfig.viewportBufferRatio);
  if (baseTiles.length === 0) {
    return [];
  }

  const n = 1 << tileConfig.zoom;
  const tilesMap = new Map<string, GeoJsonTile>();
  for (const tile of baseTiles) {
    for (let dx = -tileConfig.padding; dx <= tileConfig.padding; dx++) {
      for (let dy = -tileConfig.padding; dy <= tileConfig.padding; dy++) {
        const x = tile.x + dx;
        const y = tile.y + dy;
        if (x < 0 || x >= n || y < 0 || y >= n) {
          continue;
        }
        const key = `${x}:${y}`;
        if (!tilesMap.has(key)) {
          tilesMap.set(key, { x, y, z: tileConfig.zoom, bbox: tileBBoxXYZ(x, y, tileConfig.zoom) });
        }
      }
    }
  }

  return Array.from(tilesMap.values());
}

/**
 * Return the merged bbox of all visible + padded tiles for a viewport.
 */
export function getGeoJsonCoverageBBox(viewport: BBox, kind?: LayerKind): BBox | null {
  const tiles = getGeoJsonTilesForViewport(viewport, kind);
  if (tiles.length === 0) {
    return null;
  }

  return {
    west: Math.min(...tiles.map((tile) => tile.bbox.west)),
    east: Math.max(...tiles.map((tile) => tile.bbox.east)),
    south: Math.min(...tiles.map((tile) => tile.bbox.south)),
    north: Math.max(...tiles.map((tile) => tile.bbox.north)),
  };
}

/**
 * Expand a bbox by a ratio of its size, clamped to valid lon/lat ranges.
 */
export function bufferBBox(bbox: BBox, ratio: number): BBox {
  if (!Number.isFinite(ratio) || ratio <= 0) {
    return bbox;
  }
  const dx = bbox.east - bbox.west;
  const dy = bbox.north - bbox.south;
  const bufferX = dx * ratio;
  const bufferY = dy * ratio;
  return {
    west: Math.max(-180, bbox.west - bufferX),
    east: Math.min(180, bbox.east + bufferX),
    south: clampLat(bbox.south - bufferY),
    north: clampLat(bbox.north + bufferY),
  };
}

/**
 * Compute the WGS84 bbox of an XYZ tile at zoom z.
 */
export function tileBBoxXYZ(x: number, y: number, z: number): BBox {
  const n = 1 << z;
  const x2lon = (tx: number) => (tx / n) * 360 - 180;
  const y2lat = (ty: number) => {
    const a = Math.PI * (1 - 2 * (ty / n));
    const lat = (180 / Math.PI) * Math.atan(Math.sinh(a));
    return clampLat(lat);
  };
  return {
    west: x2lon(x),
    south: y2lat(y + 1),
    east: x2lon(x + 1),
    north: y2lat(y),
  };
}

/**
 * Stable stringify for query params (sorted key order).
 */
export function stableQueryKey(query?: Record<string, unknown>): string {
  if (!query) {
    return "";
  }
  const entries = Object.entries(query).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

/**
 * Build a cache id for a GeoJSON tile request (independent of viewport bbox).
 */
export function buildGeoJsonTileCacheId(
  req: GenericGeoJsonRequest,
  z: number,
  x: number,
  y: number,
  queryKey?: string,
  kind?: LayerKind,
): string {
  // Tile cache id is independent of the viewport bbox.
  const dt = formatDateTimeKey(req.datetime);
  const q = queryKey ? `|q:${queryKey}` : "";
  const k = kind ? `|k:${kind}` : "";
  const modelBounds = formatBoundingBoxLimitKey(req.boundingBoxLimit);
  return `geojson:${req.path}|t:${dt}${modelBounds}|z:${z}|x:${x}|y:${y}${q}${k}`;
}

/**
 * Build a cache id for a GeoJSON tile window (independent of viewport bbox).
 */
export function buildGeoJsonAggregateCacheId(
  req: GenericGeoJsonRequest,
  z: number,
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number,
  queryKey?: string,
  kind?: LayerKind,
): string {
  // Aggregate cache id for a stable tile window (reduces request spam).
  const dt = formatDateTimeKey(req.datetime);
  const q = queryKey ? `|q:${queryKey}` : "";
  const k = kind ? `|k:${kind}` : "";
  const modelBounds = formatBoundingBoxLimitKey(req.boundingBoxLimit);
  return `geojson:${req.path}|t:${dt}${modelBounds}|z:${z}|x:${xMin}-${xMax}|y:${yMin}-${yMax}${q}${k}`;
}

/**
 * Create a geometry signature using a bbox over all coordinates.
 */
export function getGeometrySignature(geom: GeoJSONFeatureCollection["features"][number]["geometry"]): string {
  if (!geom) {
    return "g:unknown";
  }

  if (geom.type === "GeometryCollection") {
    const parts = geom.geometries?.map((g) => getGeometrySignature(g)) ?? [];
    return `g:GeometryCollection|${parts.join(";")}`;
  }

  const updateBounds = (value: unknown, bounds: BBox) => {
    if (Array.isArray(value) && value.length === 2 && value.every((v) => typeof v === "number")) {
      const [lon, lat] = value as [number, number];
      bounds.west = Math.min(bounds.west, lon);
      bounds.east = Math.max(bounds.east, lon);
      bounds.south = Math.min(bounds.south, lat);
      bounds.north = Math.max(bounds.north, lat);
      return;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        updateBounds(item, bounds);
      }
    }
  };

  const bounds: BBox = {
    west: Number.POSITIVE_INFINITY,
    east: Number.NEGATIVE_INFINITY,
    south: Number.POSITIVE_INFINITY,
    north: Number.NEGATIVE_INFINITY,
  };
  updateBounds((geom as Exclude<typeof geom, { type: "GeometryCollection" }>).coordinates, bounds);

  if (!Number.isFinite(bounds.west)) {
    return `g:${geom.type}`;
  }

  return `g:${geom.type}|bbox:${bounds.west},${bounds.south},${bounds.east},${bounds.north}`;
}

/**
 * Dedup key for jet stream features based on stable properties and geometry signature.
 */
export function jetStreamsDedupKey(feature: GeoJSONFeatureCollection["features"][number]): string {
  const props = (feature.properties ?? {}) as Record<string, unknown>;
  const geomSig = getGeometrySignature(feature.geometry);
  return [
    `id:${String(props.id ?? "")}`,
    `ws:${String(props.wind_speed ?? "")}`,
    `h:${String(props.height ?? "")}`,
    `wsu:${String(props.wind_speed_unit ?? "")}`,
    `hu:${String(props.height_unit ?? "")}`,
    geomSig,
  ].join("|");
}

/**
 * Dedup key for thunderstorm track features with type-specific stable fields.
 */
export function thunderstormDedupKey(feature: GeoJSONFeatureCollection["features"][number]): string {
  const props = (feature.properties ?? {}) as Record<string, unknown>;
  const geomType = feature.geometry?.type ?? "unknown";
  const type = String(props.type ?? "?");
  const geomSig = getGeometrySignature(feature.geometry);

  // Tile responses can assign per-tile ids. Dedup avoids using id and relies on stable fields + geometry.
  if (geomType === "Point") {
    const index = props.index != null ? String(props.index) : "";
    const timestamp = props.timestamp != null ? String(props.timestamp) : "";
    const severity = props.severity != null ? String(props.severity) : "";
    return `t:${type}|g:${geomType}|i:${index}|ts:${timestamp}|sev:${severity}|${geomSig}`;
  }
  if (geomType === "LineString" || geomType === "MultiLineString") {
    return `t:${type}|g:${geomType}|${geomSig}`;
  }
  if (geomType === "Polygon" || geomType === "MultiPolygon") {
    const severity = props.severity != null ? String(props.severity) : "";
    return `t:${type}|g:${geomType}|sev:${severity}|${geomSig}`;
  }
  return `t:${type}|g:${geomType}|${geomSig}`;
}

/**
 * Merge FeatureCollections while removing duplicates using a key function.
 */
export function mergeFeatureCollectionsDedup(
  list: GeoJSONFeatureCollection[],
  dedupKeyFn: (feature: GeoJSONFeatureCollection["features"][number]) => string,
): GeoJSONFeatureCollection {
  const merged: GeoJSONFeatureCollection = { type: "FeatureCollection", features: [] };
  const seen = new Set<string>();
  for (const fc of list) {
    for (const feature of fc?.features ?? []) {
      const key = dedupKeyFn(feature);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      merged.features.push(feature);
    }
  }
  return merged;
}

/**
 * Compute visible XYZ tiles for a bbox at zoom z, with optional viewport buffer.
 */
export function getVisibleTilesWithBBox(
  bounds: BBox,
  z: number,
  viewportBufferRatio = 0,
): Array<{ x: number; y: number; z: number; bbox: BBox }> {
  const n = 1 << z;

  // Normalize inputs and expand viewport to stabilize the tile set.
  const buffered = bufferBBox(bounds, viewportBufferRatio);
  const west = normLon(buffered.west);
  const east = normLon(buffered.east);
  const south = clampLat(buffered.south);
  const north = clampLat(buffered.north);

  // y range (no wrap)
  const yMin = Math.min(lat2yIndex(north, z), lat2yIndex(south, z));
  const yMax = Math.max(lat2yIndex(north, z), lat2yIndex(south, z));

  // x ranges (anti-meridian aware)
  const xW = lon2xIndex(west, z);
  const xE = lon2xIndex(east, z);
  const spanLon = buffered.east - buffered.west;
  const coversWorld = Math.abs(spanLon) >= 360 - 1e-6;
  const xRanges = coversWorld
    ? [{ xMin: 0, xMax: n - 1 }]
    : west <= east
      ? [{ xMin: Math.min(xW, xE), xMax: Math.max(xW, xE) }]
      : [
          { xMin: xW, xMax: n - 1 },
          { xMin: 0, xMax: xE },
        ];

  const out: Array<{ x: number; y: number; z: number; bbox: BBox }> = [];
  for (const { xMin, xMax } of xRanges) {
    for (let x = xMin; x <= xMax; x++) {
      for (let y = yMin; y <= yMax; y++) {
        out.push({ x, y, z, bbox: tileBBoxXYZ(x, y, z) });
      }
    }
  }
  return out;
}

/**
 * Clamp latitude to the Web Mercator safe range.
 */
function clampLat(lat: number): number {
  return Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, lat));
}

/**
 * Normalize longitude to [-180, 180) interval.
 */
function normLon(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}

/**
 * Convert latitude to XYZ tile y-index at zoom z.
 */
function lat2yIndex(lat: number, z: number): number {
  const n = 1 << z;
  const rad = (clampLat(lat) * Math.PI) / 180;
  const merc = Math.log(Math.tan(Math.PI / 4 + rad / 2));
  const y = ((1 - merc / Math.PI) / 2) * n;
  return Math.max(0, Math.min(n - 1, Math.floor(y)));
}

/**
 * Convert longitude to XYZ tile x-index at zoom z.
 */
function lon2xIndex(lon: number, z: number): number {
  const n = 1 << z;
  const _lon = normLon(lon);
  const x = ((_lon + 180) / 360) * n;
  return Math.floor(((x % n) + n) % n); // wrap
}

/**
 * Normalize datetime to a cache key string.
 */
function formatDateTimeKey(datetime: GenericGeoJsonRequest["datetime"]): string {
  if (typeof datetime === "string") {
    return datetime;
  }
  return (datetime as any)?.toISO?.() ?? (datetime as any)?.toString?.() ?? String(datetime);
}

/**
 * Normalize model bounding box to a cache key suffix.
 */
function formatBoundingBoxLimitKey(area: GenericGeoJsonRequest["boundingBoxLimit"]): string {
  if (!area) {
    return "";
  }
  return `|mbb:${area.west},${area.south},${area.east},${area.north}`;
}
